// Imports
import { CloseIcon, MenuIcon } from '@/assets/icons'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSidebarConfig } from '@/hooks/useSidebarConfig'
import { COUNTRY_OPTIONS } from '@/multi-territory/mt-constants/mt-country-default'
import { MTIconMap } from '@/multi-territory/mt-constants/mt-icon-map'
import type { MTCountryOption } from '@/multi-territory/mt-types/mt-admin'
import { MTSidebarProps } from '@/multi-territory/mt-types/mt-shared'
import { runWithMTNavigationGuard } from '@/multi-territory/mt-utils/mt-navigation-guard'
import { ROUTES } from '@/routes/routeConfig'
import { setCountry } from '@/store/slice/countrySlice'
import { resetMTEntityTan } from '@/store/slice/mtEntityTanSlice'
import { invalidateNavModules } from '@/store/slice/navigationSlice'
import { checkSelfServiceAccess } from '@/store/slice/selfServiceSlice'
import { setCountryDetails } from '@/utils/session'
import { KeyboardArrowDown } from '@mui/icons-material'
import { FormControl, MenuItem, Select, Skeleton } from '@mui/material'
import type { UnknownAction } from '@reduxjs/toolkit'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

// Route mapping per country type
const MT_ROUTE_MAP: Record<string, string> = {
  DASHBOARD: ROUTES.DASHBOARD,
  RETURNS: ROUTES.MT_RETURNS,
  PROVISION_ADVANCE_KNOCKOFF: ROUTES.MT_PROVISION_ADVANCE,
  DOWNLOADS: ROUTES.DOWNLOAD,
  ADMIN: ROUTES.MT_ADMIN,
  SELF_SERVICE: ROUTES.SELF_SERVICE,
}

type SidebarItem = {
  code: string
  label: string
  icon: string
  route: string
  order: number
  section: string
  roles: string[]
}

// Section mapping for sidebar grouping
const SECTION_MAP: Record<string, string> = {
  DASHBOARD: 'overview',
  RETURNS: 'work',
  PROVISION_ADVANCE_KNOCKOFF: 'work',
  DOWNLOADS: 'work',
  ADMIN: 'admin',
  PREMIUM: 'admin',
  AUDIT_TRAIL: 'work',
  SELF_SERVICE: 'work',
}

// Hardcoded India sidebar items (matches screenshot order)
const INDIA_SIDEBAR_ITEMS: SidebarItem[] = [
  {
    code: 'DASHBOARD',
    label: 'Dashboard',
    icon: 'home',
    route: ROUTES.DASHBOARD,
    order: 1,
    section: 'overview',
    roles: [],
  },
  {
    code: 'RETURNS',
    label: 'Returns',
    icon: 'returns-icon',
    route: ROUTES.RETURNS,
    order: 2,
    section: 'work',
    roles: [],
  },
  {
    code: 'PROVISION_ADVANCE_KNOCKOFF',
    label: 'Provision & Advance knock off',
    icon: 'provision-icon',
    route: ROUTES.PROVISION,
    order: 3,
    section: 'work',
    roles: [],
  },
  {
    code: 'PREMIUM',
    label: 'Premium Feature',
    icon: 'premium-icon',
    route: ROUTES.PREMIUM_FEATURE,
    order: 4,
    section: 'work',
    roles: ['IT_ADMIN'],
  },
  {
    code: 'DOWNLOADS',
    label: 'Downloads',
    icon: 'download-icon',
    route: ROUTES.DOWNLOAD,
    order: 5,
    section: 'work',
    roles: [],
  },
  {
    code: 'SELF_SERVICE',
    label: 'Self Service Reports',
    icon: 'self-service',
    route: ROUTES.SELF_SERVICE,
    order: 7,
    section: 'work',
    roles: [],
  },
  {
    code: 'ADMIN',
    label: 'Admin',
    icon: 'admin-icon',
    route: ROUTES.ADMIN,
    order: 8,
    section: 'admin',
    roles: [],
  },
]

// Admin is always visible and always routes to mt/admin regardless of country.
const ADMIN_ITEM: SidebarItem = {
  code: 'ADMIN',
  label: 'Admin',
  icon: 'admin',
  route: ROUTES.MT_ADMIN,
  order: Number.MAX_SAFE_INTEGER,
  section: 'admin',
  roles: [],
}

export function MTSidebar({
  isCollapsed,
  setIsCollapsed,
  isMobileOpen,
  setIsMobileOpen,
  userRole,
  exitFullScreen,
}: MTSidebarProps) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country.countryCode
  )

  // Self Service Reports availability - drives whether the SELF_SERVICE menu item is shown
  const selfServiceEnabled = useSelector(
    (state: { selfService: { enabled: boolean } }) => state.selfService.enabled
  )

  // Check if Self Service is available
  useEffect(() => {
    dispatch(checkSelfServiceAccess() as unknown as UnknownAction)
  }, [])

  // Read navigation data from Redux (fetched in Layout.jsx)
  const {
    navModules,
    countryOptions: reduxCountryOptions,
    hasFetchedNav,
  } = useSelector((state: any) => state.navigation)

  const countryOptions: MTCountryOption[] =
    reduxCountryOptions || COUNTRY_OPTIONS

  // Fallback: config-based sidebar (handles India vs MT routes via sidebar-config.json)
  const configMenuItems = useSidebarConfig(countryCode, userRole)

  const isIndia = countryCode === GLOBAL_CONSTANTS.INDIA_COUNTRY_CODE

  // Guarantee the Admin tab is always present and always routes to mt/admin,
  // regardless of country or whether the API returned/omitted it.
  const ensureAdminItem = (items: SidebarItem[]): SidebarItem[] => {
    const result = items.map(item =>
      item.code === 'ADMIN'
        ? { ...item, route: ROUTES.MT_ADMIN, section: 'admin' }
        : item
    )
    if (!result.some(item => item.code === 'ADMIN')) {
      result.push({ ...ADMIN_ITEM })
    }
    return result
  }

  // India is fully hardcoded - never depends on the API.
  const indiaMenuItems = useMemo(
    () =>
      ensureAdminItem(
        INDIA_SIDEBAR_ITEMS.filter(
          item => item.roles.length === 0 || item.roles.includes(userRole)
        )
      ),
    [userRole]
  )

  // Resolve API modules into sidebar items (top-level only, subModules handled in content area)
  const apiMenuItems = useMemo(() => {
    if (!navModules || navModules.length === 0) return null

    const items: SidebarItem[] = navModules
      .filter(
        (mod: any) =>
          !mod.roles || mod.roles.length === 0 || mod.roles.includes(userRole)
      )
      .sort((a: any, b: any) => a.order - b.order)
      .map((mod: any) => ({
        code: mod.code,
        label: mod.label,
        icon: mod.icon,
        // Admin always routes to mt/admin (country-independent)
        route:
          mod.code === 'ADMIN'
            ? ROUTES.MT_ADMIN
            : MT_ROUTE_MAP[mod.code] || '/',
        order: mod.order,
        section: SECTION_MAP[mod.code] || 'work',
        roles: mod.roles || [],
      }))

    return ensureAdminItem(items)
  }, [navModules, userRole])

  // Resolved menu items:
  // - India: always the hardcoded list (API-independent).
  // - MT: API data when available; on API error/empty, config fallback.
  // Admin is always guaranteed in every branch.
  // Self Service Reports is shown only when access is enabled, in every branch
  // (India hardcoded, API, and config fallback).
  const menuItems: SidebarItem[] = (
    isIndia ? indiaMenuItems : apiMenuItems || ensureAdminItem(configMenuItems)
  ).filter(item => item.code !== 'SELF_SERVICE' || selfServiceEnabled)

  // Handle country change - invalidate nav so Layout re-fetches for new country
  const handleCountryChange = (event: { target: { value: string } }) => {
    const selected = countryOptions.find(
      option => option.country_code === event.target.value
    )
    if (!selected) return

    const country = {
      countryName: selected.country_name,
      countryCode: selected.country_code,
    }
    // Screens with unsaved edits (e.g. Service Master) may intercept this
    runWithMTNavigationGuard(() => {
      dispatch(setCountry(country))
      setCountryDetails(country)
      dispatch(invalidateNavModules())
      dispatch(resetMTEntityTan())
      navigate(ROUTES.DASHBOARD)
    })
  }

  // Handle nav item click - collapse sidebar on selection
  const handleNavigation = (route: string) => {
    runWithMTNavigationGuard(() => {
      navigate(route)
      exitFullScreen()
      setIsCollapsed(true)
      if (isMobileOpen) setIsMobileOpen(false)
    })
  }

  // Active route check
  const isActive = (route: string) =>
    location.pathname === route ||
    (route !== ROUTES.DASHBOARD && location.pathname.startsWith(`${route}/`))

  // Render a single menu item
  const renderItem = (item: SidebarItem, isMobile: boolean) => {
    const Icon =
      MTIconMap[item.icon] ||
      MTIconMap[item.icon.replace('-icon', '')] ||
      MTIconMap.home
    const active = isActive(item.route)

    return (
      <button
        key={item.code}
        type="button"
        onClick={() => handleNavigation(item.route)}
        aria-current={active ? 'page' : undefined}
        aria-label={`Navigate to ${item.label}`}
        className={`w-full h-12 flex items-center overflow-hidden transition-colors ${
          active
            ? 'bg-[var(--bg-brand-layer-2)]'
            : 'hover:bg-[var(--bg-layer-1)]'
        } ${!isMobile && isCollapsed ? 'justify-center' : 'justify-start'}`}
      >
        <Icon className="w-12 h-12 shrink-0" isSelected={active} />
        {(isMobile || !isCollapsed) && (
          <span className="pr-3 text-base text-[var(--forbrand-primary)] whitespace-nowrap overflow-hidden text-ellipsis">
            {item.label}
          </span>
        )}
      </button>
    )
  }

  // Render skeleton loading state
  const renderSkeleton = (expanded: boolean) => {
    return (
      <div className="h-full flex flex-col bg-white">
        <div
          className={`h-[84px] flex items-center shrink-0 ${
            expanded ? 'px-6' : 'justify-center'
          }`}
        >
          <Skeleton variant="circular" width={32} height={32} />
        </div>
        <div className={`flex-1 pb-6 ${expanded ? 'px-5' : 'px-2'}`}>
          <div className="space-y-2 mb-4">
            <Skeleton variant="rounded" height={48} />
          </div>
          {expanded && (
            <div className="my-5 border-t border-[var(--border-subtel)]" />
          )}
          {expanded && (
            <div className="mb-4">
              <Skeleton variant="text" width={60} height={24} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={48} />
            </div>
          )}
          <div className="space-y-2">
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={48} />
          </div>
          {expanded && (
            <div className="my-5 border-t border-[var(--border-subtel)]" />
          )}
          <div className="space-y-2">
            <Skeleton variant="rounded" height={48} />
          </div>
        </div>
      </div>
    )
  }

  // Render sidebar content (shared between desktop and mobile)
  const renderContent = (isMobile: boolean) => {
    const expanded = isMobile || !isCollapsed

    // Only show the skeleton for MT while the nav fetch is still pending.
    // India never waits on the API, and once the fetch has completed (even on
    // error) we fall through to the config fallback so the sidebar always renders.
    if (!isIndia && !hasFetchedNav && !apiMenuItems) {
      return renderSkeleton(expanded)
    }

    const overviewItems = menuItems.filter(item => item.section === 'overview')
    const workItems = menuItems.filter(item => item.section === 'work')
    const adminItems = menuItems.filter(item => item.section === 'admin')
    const selectId = isMobile ? 'mobile-country' : 'desktop-country'

    return (
      <div className="h-full flex flex-col bg-white">
        <div
          className={`h-[84px] flex items-center shrink-0 ${
            expanded ? 'px-6' : 'justify-center'
          }`}
        >
          <button
            type="button"
            onClick={() =>
              isMobile ? setIsMobileOpen(false) : setIsCollapsed(!isCollapsed)
            }
            className="p-2"
            aria-label={isMobile ? 'Close navigation' : 'Toggle navigation'}
          >
            {isMobile ? (
              <CloseIcon className="h-5 w-5" />
            ) : (
              <MenuIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        <nav
          className={`flex-1 overflow-y-auto hide-scrollbar pb-6 ${
            expanded ? 'px-5' : 'px-2'
          }`}
          aria-label="Main navigation"
        >
          <div className="space-y-2">
            {overviewItems.map(item => renderItem(item, isMobile))}
          </div>

          {expanded && (
            <div className="my-5 border-t border-[var(--border-subtel)]" />
          )}

          {expanded && (
            <div className="mb-4">
              <label
                htmlFor={selectId}
                className="block mb-2 text-base text-[var(--forbrand-primary)]"
              >
                Country
              </label>
              <FormControl fullWidth size="small">
                <Select
                  id={selectId}
                  value={countryCode}
                  onChange={handleCountryChange}
                  displayEmpty
                  IconComponent={KeyboardArrowDown}
                  renderValue={(value: string) =>
                    countryOptions.find(option => option.country_code === value)
                      ?.country_name || 'Select'
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        maxWidth: 200,
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                        },
                      },
                    },
                  }}
                  sx={{
                    height: 48,
                    borderRadius: 0,
                    fontSize: 16,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--border-strong)',
                    },
                  }}
                >
                  {countryOptions.map(option => (
                    <MenuItem
                      key={option.uuid || option.country_code}
                      value={option.country_code}
                    >
                      {option.country_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          <div className="space-y-2">
            {workItems.map(item => renderItem(item, isMobile))}
          </div>

          {expanded && adminItems.length > 0 && (
            <div className="my-5 border-t border-[var(--border-subtel)]" />
          )}

          <div className="space-y-2">
            {adminItems.map(item => renderItem(item, isMobile))}
          </div>
        </nav>
      </div>
    )
  }

  return (
    <>
      <aside
        className={`hidden lg:block bg-white border-r border-[var(--border-subtel)] transition-[width] duration-300 shrink-0 ${
          isCollapsed ? 'w-[65px]' : 'w-[300px]'
        }`}
      >
        {renderContent(false)}
      </aside>

      {isMobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-label="Close navigation overlay"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[300px] bg-white border-r border-[var(--border-subtel)] transform transition-transform duration-300 lg:hidden ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {renderContent(true)}
      </aside>
    </>
  )
}
